import { useEffect, useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Search, X } from 'lucide-react';

// Callers supply authorized links only. Searching never broadens access.
export function currentNavigationItem(sections, pathname) {
  return sections.flatMap(section => section.items.map(item => ({ ...item, section: section.title })))
    .filter(item => pathname === item.path || (item.path.split('/').length > 2 && pathname.startsWith(`${item.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export default function PortalNavigation({ sections, collapsed }) {
  const { pathname } = useLocation();
  const id = useId();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const current = currentNavigationItem(sections, pathname);
  const currentSection = current?.section;
  useEffect(() => {
    if (currentSection) setExpanded(previous => ({ ...previous, [currentSection]: true }));
    setSearch('');
  }, [pathname, currentSection]);
  const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const visible = sections.map(section => ({ ...section, items: section.items.filter(item =>
    terms.every(term => `${section.title} ${item.label}`.toLowerCase().includes(term))) })).filter(section => section.items.length);
  return <nav className="erp-navigation flex-1 overflow-y-auto sidebar-scroll px-3" aria-label="Main navigation">
    {!collapsed && <div className="erp-navigation-tools">
      <div className="erp-navigation-search">
        <Search size={15} aria-hidden="true" />
        <input aria-label="Find a page" placeholder="Find a page…" value={search} onChange={event => setSearch(event.target.value)} />
        {search && <button type="button" aria-label="Clear page search" onClick={() => setSearch('')}><X size={14} /></button>}
      </div>
      <div className="erp-navigation-expand">
        <span>{search ? 'Matching pages' : 'Your workspace'}</span>
        <button type="button" onClick={() => setExpanded(Object.fromEntries(sections.map(section => [section.title, true])))}>Expand all</button>
      </div>
    </div>}
    {!visible.length && <p className="px-3 py-5 text-sm text-gray-400" role="status">No accessible pages match.</p>}
    {visible.map((section, index) => {
      const open = collapsed || !!terms.length || (expanded[section.title] ?? (section.title === 'Core' || sections.length === 1));
      const regionId = `${id}-section-${index}`;
      return <section className="erp-navigation-section" key={section.title} aria-label={section.title}>
        {!collapsed && <button type="button" className="erp-navigation-heading" aria-expanded={open} aria-controls={regionId}
          onClick={() => setExpanded(previous => ({ ...previous, [section.title]: !open }))}>
          <span>{section.title}</span><ChevronDown size={14} className={open ? '' : '-rotate-90'} aria-hidden="true" />
        </button>}
        <div id={regionId} hidden={!open} className="space-y-1">
          {section.items.map(item => {
            const Icon = item.icon;
            return <NavLink key={item.path} to={item.path} end aria-label={item.label} title={collapsed ? item.label : undefined}
              className={({ isActive }) => `erp-navigation-link flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition ${isActive || current?.path === item.path ? 'bg-amber-400/20 text-amber-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              {Icon && <Icon size={18} className="shrink-0" aria-hidden="true" />}{!collapsed && <span>{item.label}</span>}
            </NavLink>;
          })}
        </div>
      </section>;
    })}
  </nav>;
}
